<template>
  <v-dialog
    :value="value"
    max-width="680"
    content-class="rounded-lg"
    @input="$emit('input', $event)"
  >
    <v-card dark>
      <v-card-title class="d-flex align-center">
        <v-icon left class="mr-1">mdi-information-outline</v-icon>
        <span>Engraving Generator — Instructions</span>
        <v-spacer />
        <v-btn icon small @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text class="instructions-content pt-4 px-4 pb-4">
        <p class="instructions-intro text-body-2 mb-4">
          This tool creates engraving-ready images for the order in three steps: select a type and source image, generate AI edits, then combine one or more images into a mask (circle, oval, heart, or rectangle) for download.
        </p>

        <h3 class="instructions-h3">Step 1 — Select Image</h3>
        <ul class="instructions-list">
          <li><strong>Order details</strong> (left): Shows order number, line items, customer, delivery, and notes. Use this to confirm you are working on the correct order.</li>
          <li><strong>Type selection</strong> (middle): Choose how the image will be processed.
            <ul class="instructions-sublist">
              <li><strong>Predefined</strong>: Pick a type (Fingerprint, Nose Print, Paw Print, Baby Hands, Baby Feet, Handwriting). The AI will use a preset prompt for that type.</li>
              <li><strong>Custom Prompt</strong>: Enter your own text prompt and choose the AI model (Gemini 2.5 or Gemini 3).</li>
            </ul>
          </li>
          <li><strong>Image gallery</strong> (right): Shows images already attached to the order (e.g. from metafield or line-item uploads). You can <strong>Upload from PC</strong> to add more. Next to it, <strong>Backside engraving</strong> opens a modal to create text-based backside SVGs (see below). Click one image to select it (blue border and checkmark). You must select exactly one image to continue.</li>
          <li><strong>Backside engraving</strong>: The button (with a badge when the order has eligible line items) opens a dialog. Choose a <strong>line item</strong>, enter up to four <strong>text lines</strong>, pick a <strong>font</strong> (Modern, Classic, or Italic), then click <strong>Generate preview</strong>. The preview appears on the right; use the download icon on the preview to save the SVG, or the image is automatically added to your <strong>Backside engravings</strong> section below the gallery. You can generate multiple backside engravings; they appear in Step 1 (display only) and in Step 3 where they can be used in the mask.</li>
          <li>Click <strong>Next</strong> to go to Step 2. The <strong>X</strong> button (top right) closes the tool without saving. <strong>Previous</strong> goes back one step.</li>
        </ul>

        <h3 class="instructions-h3">Step 2 — Generate edits</h3>
        <ul class="instructions-list">
          <li><strong>Left panel — Generate edits</strong>: The header shows a badge with the type you chose in Step 1 (e.g. Paw Print). Select an input image in the gallery below, then click the blue <strong>Generate edits</strong> button to send it to the AI. While it runs, a <strong>loading section</strong> (“Generating your edits” and progress bar) appears, and the <strong>input gallery</strong> stays visible. When done, you can run again (e.g. after changing the type in Step 1) to get new variations.</li>
          <li><strong>Right panel — Generated edits</strong>: Two sections, in order:
            <ul class="instructions-sublist">
              <li><strong>Newly generated</strong>: The latest set of edits from the last run (skeleton placeholders while loading). When you start a new run, the previous “newly generated” edits move into “Previously generated” so they remain visible.</li>
              <li><strong>Previously generated</strong>: Edits from earlier runs or from the backend for this order. You can use any of these in Step 3.</li>
            </ul>
          </li>
          <li>Click an edit thumbnail to <strong>view it fullscreen</strong>. Edits in this step are view-only (not selectable); in Step 3 you will pick from the full gallery (new + previous + uploads). Click <strong>Next</strong> when ready.</li>
        </ul>

        <h3 class="instructions-h3">Step 3 — Masking</h3>
        <ul class="instructions-list">
          <li><strong>Left panel — Inputs</strong>: The main gallery lists all images you can use (newly generated, previously generated, and any uploaded in Step 3). A separate <strong>Backside engravings</strong> section shows any backside SVGs you created in Step 1 (or add more via the <strong>Backside engraving</strong> button here). All of these can be selected for the mask. Depending on the mask:
            <ul class="instructions-sublist">
              <li><strong>1 part</strong>: Select one image.</li>
              <li><strong>2 or 3 parts</strong>: Select two or three images in order (first selected = part 1, second = part 2, etc.). You can upload more via the gallery if needed.</li>
            </ul>
          </li>
          <li><strong>Settings icon (Mask options)</strong>: Opens a dialog where you set:
            <ul class="instructions-sublist">
              <li><strong>Number of parts</strong>: 1, 2, or 3 images in the mask.</li>
              <li><strong>Direction</strong>: Horizontal or vertical layout of the parts.</li>
              <li><strong>Shape</strong>: Circle, oval, heart, Rectangle (L), or Rectangle (M).</li>
              <li><strong>Spacing</strong>: Gap between parts.</li>
              <li><strong>Output size</strong>: Final image size in pixels.</li>
            </ul>
          </li>
          <li><strong>Right panel — Preview</strong>: Shows the combined mask. Once you have selected the required number of images, the preview updates. Click the <strong>download icon</strong> to save the mask image. The shape on the right does not open a file picker; all images must be chosen from the left gallery.</li>
          <li>When finished, click <strong>Done</strong> (where the Next button is) to close the tool, or use the <strong>X</strong> button.</li>
        </ul>

        <p class="instructions-tips mt-4">
          <strong>Tips:</strong> Ensure at least one image is available in Step 1 (order has uploads or metafield images) so the gallery is not empty. For best results, use a clear source image in Step 1 and pick a type that matches the content (e.g. Paw Print for pet paws). Use <strong>Backside engraving</strong> when the order has personal text for the back of the product (e.g. name and date); generated SVGs can be downloaded or used as mask parts in Step 3.
        </p>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn color="primary" text @click="$emit('input', false)">
          Got it
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'InstructionsModal',
  props: {
    value: { type: Boolean, default: false },
  },
}
</script>

<style scoped>
.instructions-content {
  color: rgba(255, 255, 255, 0.88);
  max-height: 70vh;
  overflow-y: auto;
}
.instructions-intro {
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.5;
}
.instructions-h3 {
  font-size: 1rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.98);
  margin: 1rem 0 0.5rem;
  padding-bottom: 4px;
}
.instructions-h3:first-of-type {
  margin-top: 0;
}
.instructions-list {
  margin: 0 0 0 1rem;
  padding-left: 0.5rem;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.82);
}
.instructions-list li {
  margin-bottom: 0.35rem;
}
.instructions-sublist {
  margin: 0.25rem 0 0 1rem;
  padding-left: 0.5rem;
  list-style-type: circle;
}
.instructions-sublist li {
  margin-bottom: 0.2rem;
}
.instructions-tips {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  border-left: 3px solid rgba(66, 165, 245, 0.6);
}
</style>
